import { useAuth } from '@/contexts/auth-context';
import { useSwitchBrandAction } from '@/hooks/actions/use-switch-brand-action';
import { useOrganizationBrandsQuery } from '@/hooks/queries/use-organization-brands-query';
import { Button, Flex, Stack, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

/**
 * The way out of an onboarding step.
 *
 * `SubscriptionGuard` sends every dashboard route here as long as the current
 * brand has no Shopify store, so a brand that has none to connect takes the
 * whole organization down with it. Renders nothing when the organization holds
 * no other brand, which is the case of a first signup.
 */
export const OnboardingBrandSwitch = () => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const { data: brands } = useOrganizationBrandsQuery();
  const switchBrandMutation = useSwitchBrandAction();

  const otherBrands = (brands ?? []).filter((brand) => !brand.isCurrent);

  if (!isAuthenticated || otherBrands.length === 0) {
    return null;
  }

  return (
    <Stack gap="9px" mt="18px" align="center">
      <Text fontFamily="body" fontSize="12px" color="#a89f8f" fontWeight="300">
        {t('onboarding.switchBrand.label')}
      </Text>
      <Flex gap="8px" wrap="wrap" justify="center">
        {otherBrands.map((brand) => (
          <Button
            key={brand.id}
            variant="outline"
            size="xs"
            borderRadius="full"
            loading={switchBrandMutation.isPending}
            onClick={() => switchBrandMutation.mutate(brand.id)}
          >
            {brand.name}
          </Button>
        ))}
      </Flex>
    </Stack>
  );
};
